import { useCallback, useEffect, useRef, useState } from "react";
import { showInfo, showWarning } from "@infrastructure/api/utils/toastNotifications";
import {
    ActivityContext,
    ActivityKpiContext,
    getActivityKpiContexts,
    getKpiValueKey,
} from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import {
    ACTIVITY_ENTRY_AUTOSAVE_INTERVAL_MS,
    ACTIVITY_ENTRY_SAVE_REMINDER_MS,
} from "@features/report/activity-entry/utils/activityEntryPreferences";
import {
    isCalculatedEntryMode,
    isBooleanDataType,
    normalizeBooleanInputValue,
} from "@core/reports/utils/fieldTypeUtils";
import type {
    ActivityEntryFormGroup,
    DraftEntry,
    SaveDraftPayload,
} from "@features/report/activity-entry/types/activityEntry";
import {
    buildFormValuesFromGroups,
    collectCalculatedValueKeys,
} from "@features/report/activity-entry/utils/activityEntryFormValues";

type SaveDraftResponse = { isSuccess: () => boolean };

export interface UseActivityEntryDraftingParams {
    enabled: boolean;
    activityEditable: boolean;
    /** External busy (API action / submit) — does not include this hook's syncBusy. */
    busy: boolean;
    tagIsClosed: boolean;
    autoSaveEnabled: boolean;
    entityId: number;
    reportTagId: number;
    showInactive: boolean;
    /** `${scope}-${entityId}-${reportTemplateId}-${reportTagId}` — resets the save-reminder when it changes. */
    hydrationKey: string;
    values: Record<string, string>;
    allKpiContexts: ActivityKpiContext[];
    unsavedCount: number;
    /** Syncs the dirty-baseline after a successful save (from useActivityEntryForm). */
    markSaved: (payload: SaveDraftPayload) => void;
    /** Imperatively merge post-reload server values into form state. */
    applyServerValues: (
        serverValues: Record<string, string>,
        forceKeys?: Iterable<string>
    ) => void;
    doSaveDraft: (
        entityId: number,
        payload: SaveDraftPayload,
        options?: { silent?: boolean }
    ) => Promise<SaveDraftResponse>;
    reload: () => Promise<ActivityEntryFormGroup[] | null>;
    loadParentDepartmentReport: () => Promise<unknown>;
    /** Modal open state — the Ctrl+S hotkey is suppressed while a modal is open. */
    showSubmitConfirm: boolean;
    showReviewModal: boolean;
    /** KPI grid maximize modal — also pauses autosave. */
    gridModalOpen: boolean;
}

/**
 * Owns the activity entry form's draft persistence: payload builders, the save
 * call (which syncs the dirty-baseline via markSaved and reloads), the "save all"
 * orchestration, and the autosave / save-reminder / Ctrl+S effects. Extracted verbatim
 * from ActivityEntryForm — effect bodies and dependency arrays preserved exactly.
 */
export function useActivityEntryDrafting({
    enabled,
    activityEditable,
    busy,
    tagIsClosed,
    autoSaveEnabled,
    entityId,
    reportTagId,
    showInactive,
    hydrationKey,
    values,
    allKpiContexts,
    unsavedCount,
    markSaved,
    applyServerValues,
    doSaveDraft,
    reload,
    loadParentDepartmentReport,
    showSubmitConfirm,
    showReviewModal,
    gridModalOpen,
}: UseActivityEntryDraftingParams) {
    const saveReminderShownRef = useRef(false);
    const unsavedSinceRef = useRef<number | null>(null);
    const saveInFlightRef = useRef(false);
    const [syncBusy, setSyncBusy] = useState(false);
    const formBlocked = busy || syncBusy;

    // Reset the save-reminder when the selection changes.
    useEffect(() => {
        saveReminderShownRef.current = false;
        unsavedSinceRef.current = null;
    }, [hydrationKey]);

    const buildDraftPayloadForActivity = useCallback(
        (context: ActivityContext): SaveDraftPayload => {
            const report_template_id = context.group.report_template.report_template_id;

            const entries: DraftEntry[] = getActivityKpiContexts(context, showInactive)
                .filter(({ kpi }) => !isCalculatedEntryMode(kpi.entry_mode))
                .map(({ kpi }) => {
                    const key = getKpiValueKey(
                        context.subGroupId,
                        context.activity.activity_id,
                        kpi.kpi_id
                    );
                    const currentValue = isBooleanDataType(kpi.data_type?.data_type_name ?? "")
                        ? normalizeBooleanInputValue(values[key])
                        : (values[key] ?? "").toString().trim();

                    return {
                        report_template_id,
                        activity_id: context.activity.activity_id,
                        kpi_id: kpi.kpi_id,
                        kpi_value: currentValue,
                        report_tag_id: reportTagId,
                        report_sub_group_id: context.subGroupId,
                    };
                });

            return { entries };
        },
        [showInactive, values, reportTagId]
    );

    const handleSaveDraft = useCallback(
        async (payload: SaveDraftPayload, options: { silent?: boolean } = {}) => {
            if (saveInFlightRef.current || formBlocked) {
                return { isSuccess: () => false };
            }

            saveInFlightRef.current = true;
            setSyncBusy(true);
            try {
                const response = await doSaveDraft(entityId, payload, options);
                if (!response.isSuccess()) return response;

                // After success → sync baseline with current values, then reload server data
                markSaved(payload);

                saveReminderShownRef.current = false;
                unsavedSinceRef.current = null;

                const groups = await reload();
                if (groups) {
                    const savedKeys = payload.entries.map((entry) =>
                        getKpiValueKey(
                            entry.report_sub_group_id ?? null,
                            entry.activity_id,
                            entry.kpi_id
                        )
                    );
                    const forceKeys = [
                        ...savedKeys,
                        ...collectCalculatedValueKeys(groups, showInactive),
                    ];
                    applyServerValues(
                        buildFormValuesFromGroups(groups, showInactive),
                        forceKeys
                    );
                }
                await loadParentDepartmentReport();
                return response;
            } finally {
                setSyncBusy(false);
                saveInFlightRef.current = false;
            }
        },
        [
            entityId,
            doSaveDraft,
            formBlocked,
            loadParentDepartmentReport,
            reload,
            markSaved,
            applyServerValues,
            showInactive,
        ]
    );

    const buildDraftPayloadForAll = useCallback((): SaveDraftPayload => {
        const entries: DraftEntry[] = allKpiContexts
            .filter(({ kpi }) => !isCalculatedEntryMode(kpi.entry_mode))
            .map(({ group, subGroupId, activity, kpi }) => {
                const key = getKpiValueKey(subGroupId, activity.activity_id, kpi.kpi_id);
                const currentValue = isBooleanDataType(kpi.data_type?.data_type_name ?? "")
                    ? normalizeBooleanInputValue(values[key])
                    : (values[key] ?? "").toString().trim();

                return {
                    report_template_id: group.report_template.report_template_id,
                    activity_id: activity.activity_id,
                    kpi_id: kpi.kpi_id,
                    kpi_value: currentValue,
                    report_tag_id: reportTagId,
                    report_sub_group_id: subGroupId,
                };
            });

        return { entries };
    }, [allKpiContexts, reportTagId, values]);

    const saveAllDrafts = useCallback(
        async (mode: "manual" | "hotkey" | "auto") => {
            if (!activityEditable || formBlocked) return null;
            if (tagIsClosed) {
                if (mode !== "auto") {
                    showInfo("This reporting period is not open. Saving is unavailable.");
                }
                return null;
            }

            if (allKpiContexts.length === 0) {
                if (mode !== "auto") {
                    showInfo("There are no activity entries available to save.");
                }
                return null;
            }

            if (unsavedCount === 0) {
                if (mode !== "auto") {
                    showInfo("All changes are already saved.");
                }
                return null;
            }

            try {
                return await handleSaveDraft(buildDraftPayloadForAll(), {
                    silent: mode === "auto",
                });
            } catch {
                return null;
            }
        },
        [
            activityEditable,
            allKpiContexts.length,
            buildDraftPayloadForAll,
            formBlocked,
            handleSaveDraft,
            tagIsClosed,
            unsavedCount,
        ]
    );

    useEffect(() => {
        if (!enabled || !activityEditable || unsavedCount === 0) {
            unsavedSinceRef.current = null;
            saveReminderShownRef.current = false;
            return;
        }

        if (unsavedSinceRef.current === null) {
            unsavedSinceRef.current = Date.now();
        }
    }, [activityEditable, enabled, unsavedCount]);

    useEffect(() => {
        if (
            !enabled ||
            !activityEditable ||
            !autoSaveEnabled ||
            formBlocked ||
            gridModalOpen ||
            unsavedCount === 0
        ) {
            return;
        }

        const timer = window.setTimeout(() => {
            void saveAllDrafts("auto");
        }, ACTIVITY_ENTRY_AUTOSAVE_INTERVAL_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        activityEditable,
        autoSaveEnabled,
        formBlocked,
        enabled,
        gridModalOpen,
        saveAllDrafts,
        unsavedCount,
    ]);

    useEffect(() => {
        if (
            !enabled ||
            !activityEditable ||
            formBlocked ||
            unsavedCount === 0 ||
            saveReminderShownRef.current
        ) {
            return;
        }

        const unsavedSince = unsavedSinceRef.current ?? Date.now();
        const remainingMs = Math.max(
            0,
            ACTIVITY_ENTRY_SAVE_REMINDER_MS - (Date.now() - unsavedSince)
        );

        const timer = window.setTimeout(() => {
            if (saveReminderShownRef.current) return;

            saveReminderShownRef.current = true;
            showWarning(
                "You are caught up so far. Save your draft now to protect what you have already entered."
            );
        }, remainingMs);

        return () => {
            window.clearTimeout(timer);
        };
    }, [activityEditable, formBlocked, enabled, unsavedCount]);

    useEffect(() => {
        if (!enabled || !activityEditable) return;

        const handleSaveHotKey = (event: KeyboardEvent) => {
            const isSaveKey =
                (event.ctrlKey || event.metaKey) &&
                event.key.toLowerCase() === "s";

            if (!isSaveKey) return;

            event.preventDefault();

            if (showSubmitConfirm || showReviewModal || gridModalOpen) return;

            void saveAllDrafts("hotkey");
        };

        window.addEventListener("keydown", handleSaveHotKey);

        return () => {
            window.removeEventListener("keydown", handleSaveHotKey);
        };
    }, [
        activityEditable,
        enabled,
        gridModalOpen,
        saveAllDrafts,
        showReviewModal,
        showSubmitConfirm,
    ]);

    return {
        buildDraftPayloadForActivity,
        buildDraftPayloadForAll,
        handleSaveDraft,
        saveAllDrafts,
        syncBusy,
    };
}
